import { Box, Stack } from '@mui/material';
import { Button, colors, Typography } from '@sweep-io/sweep-design';
import { useDispatch, useSelector } from 'react-redux';
import { useSweepApi } from '../../../apis/sweep';
import { useIntegrations } from '../../../hooks/useIntegrations';
import {
  selectSlackIntegrationStatus,
  selectSlackUpgradeStatus,
  setSlackIntegrationStatus,
  SlackConnectionStatus,
} from '../../../reducers/integrationsReducer';
import { useRunOnce } from '../../common/useRunOnce';
import { waitForWindowToClose } from '../../pages/helper';
import { randomId, setSlackStateToken } from '../../../utils/slackOAuthStateUtils';
import RestrictedTooltip from '../../common/permissions/RestrictedTooltip';
import useCheckPermissionsForCurrentCrmOrg from '../../common/permissions/useCurrentCrmOrgPermissions';
import { ButtonSize, ButtonVariants } from '@sweep-io/sweep-design/dist/components/Button/types';
import usePopover from '../../../hooks/usePopover';
import { ActionsRequireMp } from '../../common/install-managed-package/getInstallManagedPackageText';
import InstallManagedPackagePopoverContent from '../../common/install-managed-package/InstallManagedPackagePopoverContent';
import IntegrationsButtonPopover from './IntegrationsButtonPopover';
import { ACTIONS_EVENTS } from '../../../services/events';
import useSendBiEvent from '../../../hooks/useSendBiEvent';
import { ActionsMenu } from '../../common/actions-menu/ActionsMenu';
import { Unplug } from '@sweep-io/sweep-design/dist/icons';

const SERVER_URL = import.meta.env.VITE_SERVER_URL;

const getSlackRedirectToOAuthUrl = (state: string) =>
  `${SERVER_URL}/data-integrations/slack/redirect-to-oauth?state=${state}`;

interface SlackConnectButtonProps {
  variant?: ButtonVariants;
  label?: string;
  endIconName?: string;
  size?: ButtonSize;
  disabled?: boolean;
  onSuccess?: () => void;
}

export const SlackConnectButton = ({
  variant = 'system',
  label,
  endIconName,
  size = 'tiny',
  disabled,
  onSuccess,
}: SlackConnectButtonProps) => {
  const sendBiEvent = useSendBiEvent();
  const slackStatus = useSelector(selectSlackIntegrationStatus);
  const shouldDisplayUpgrade = useSelector(selectSlackUpgradeStatus);
  const sweepApi = useSweepApi();
  const dispatch = useDispatch();
  const integrations = useIntegrations();
  const managedPackagePopover = usePopover();

  const [isConnectSlackAllowed, isDisconnectSlackAllowed] = useCheckPermissionsForCurrentCrmOrg([
    'create:data-integrations:slack:oauth',
    'delete:data-integrations:slack',
  ]);

  useRunOnce(async () => {
    await integrations.getSlackStatus();
  });

  const connectToSlack = async () => {
    sendBiEvent({ name: ACTIONS_EVENTS.slackConnect });
    const id = randomId(15);
    setSlackStateToken(id);
    const _window = window.open(getSlackRedirectToOAuthUrl(id), '_blank');

    if (_window) {
      dispatch(setSlackIntegrationStatus(SlackConnectionStatus.Initialization));
      await waitForWindowToClose(_window);
      const status = await integrations.getSlackStatus();
      if (status === SlackConnectionStatus.Connected && onSuccess) {
        onSuccess();
      }
    } else {
      dispatch(setSlackIntegrationStatus(SlackConnectionStatus.Error));
    }
  };

  const disconnectSlack = () => {
    sweepApi.delete(`/data-integrations/slack`);
    dispatch(setSlackIntegrationStatus(SlackConnectionStatus.Initialization));
  };
  return (
    <>
      <IntegrationsButtonPopover
        open={managedPackagePopover.isPopoverOpen}
        anchorEl={managedPackagePopover.anchorElement}
        onClose={() => managedPackagePopover.closePopover()}
      >
        <InstallManagedPackagePopoverContent action={ActionsRequireMp.SLACK} />
      </IntegrationsButtonPopover>
      <Stack direction="row" gap="4px">
        {shouldDisplayUpgrade && (
          <RestrictedTooltip
            to={['create:data-integrations:slack:oauth']}
            notAllowedTitle={'To adjust integration settings, please contact your admin.'}
          >
            <Button
              variant={variant}
              disabled={!isConnectSlackAllowed || disabled}
              loading={SlackConnectionStatus.Initialization === slackStatus}
              onClick={() => connectToSlack()}
              endIconName={endIconName}
              size={size}
              ref={managedPackagePopover.anchorRef}
            >
              {label ? label : 'Upgrade'}
            </Button>
          </RestrictedTooltip>
        )}
        {SlackConnectionStatus.Connected !== slackStatus && !shouldDisplayUpgrade ? (
          <RestrictedTooltip
            to={['create:data-integrations:slack:oauth']}
            notAllowedTitle={'To adjust integration settings, please contact your admin.'}
          >
            <Button
              variant={variant}
              disabled={!isConnectSlackAllowed || disabled}
              loading={SlackConnectionStatus.Initialization === slackStatus}
              onClick={() => connectToSlack()}
              endIconName={endIconName}
              size={size}
              ref={managedPackagePopover.anchorRef}
            >
              {label ? label : 'Connect'}
            </Button>
          </RestrictedTooltip>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" color={colors.feedback.success}>
              Connected
            </Typography>
            <ActionsMenu
              iconSize="tiny"
              actions={[
                {
                  label: 'Disconnect',
                  value: 'Disconnect',
                  actionIcon: <Unplug color={colors.blush[600]} />,
                  disabled: !isDisconnectSlackAllowed,
                  disabledTooltip: 'To adjust integration settings, please contact your admin.',
                  actionSx: { color: colors.blush[600] },
                },
              ]}
              onClick={(action) => {
                if (action?.value === 'Disconnect') {
                  disconnectSlack();
                }
              }}
              iconVariant="system"
              menuWidth="180px"
            />
          </Box>
        )}
      </Stack>
    </>
  );
};
